/**
 * Module-level singleton that keeps a ScriptPlaybackEngine alive outside the
 * ScriptPlayerModal. Used for "keep playing in background" mode.
 *
 * The engine updates useScriptPlaybackStore so the existing ScriptMiniPlayer
 * can show progress. The control functions here route back to the engine.
 */

import type { ScriptPlaybackEngine } from "./script-playback-engine"
import { useScriptPlaybackStore } from "./script-playback-store"
import type { UserScript } from "./script-store"

let _engine: ScriptPlaybackEngine | null = null

export function getBackgroundEngine(): ScriptPlaybackEngine | null {
  return _engine
}

export function isBackgroundActive(): boolean {
  return _engine !== null
}

/** Hand off a running engine to background mode. */
export function startBackground(
  engine: ScriptPlaybackEngine,
  script: UserScript,
  totalDurationMs: number,
) {
  // Stop any previous background session
  _engine?.stop()
  _engine = engine

  const store = useScriptPlaybackStore.getState()
  store.startScript(script.id, script.name, false, totalDurationMs / 1000)
  store.registerControls({
    pause: pauseBackground,
    resume: resumeBackground,
    stop: stopBackground,
  })

  const unsubTime = engine.onTimeUpdate((ms) => {
    useScriptPlaybackStore.getState().updateTime(ms / 1000)
  })

  const unsubState = engine.onStateChange((state) => {
    const s = useScriptPlaybackStore.getState()
    if (state === "playing") s.resumeScript()
    if (state === "paused") s.pauseScript()
    if (state === "stopped" || state === "idle") {
      s.stopScript() // also clears controls
      unsubTime()
      unsubState()
      _engine = null
    }
  })
}

export function pauseBackground() {
  _engine?.pause()
}

export function resumeBackground() {
  _engine?.resume()
}

export function stopBackground() {
  _engine?.stop()
  _engine = null
  useScriptPlaybackStore.getState().stopScript()
}
